import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, LayoutDashboard } from "lucide-react";
import { NAV_ITEMS, SITE_CONFIG } from "@/data/siteConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [postcode, setPostcode] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    if (postcode.trim()) {
      navigate(`/search?postcode=${encodeURIComponent(postcode.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
      setPostcode("");
    }
  };

  const SearchForm = ({ className = "" }: { className?: string }) => (
    <form onSubmit={handleSearch} className={`flex gap-2 ${className}`}>
      <Input
        name="postcode"
        placeholder={SITE_CONFIG.searchPlaceholder}
        value={postcode}
        onChange={(e) => setPostcode(e.target.value)}
        required
        className="h-10 flex-1"
        autoFocus={false}
      />
      <Button type="submit" size="sm" className="h-10 px-4">
        <Search className="mr-1.5 h-4 w-4" />
        Go
      </Button>
    </form>
  );

  return (
    <nav className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="text-xl font-bold text-primary" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {SITE_CONFIG.name}
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 lg:gap-2 md:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary lg:px-3"
            >
              {item.label}
            </Link>
          ))}
          <Link
            to={user ? "/dashboard" : "/practice-login"}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 lg:px-3"
          >
            <LayoutDashboard className="h-4 w-4" />
            {user ? "Dashboard" : "Practice Login"}
          </Link>
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" className="ml-1 whitespace-nowrap">
                <Search className="mr-1.5 h-4 w-4" />
                Search
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-3">
              <p className="mb-2 text-sm font-medium text-foreground">Search by postcode</p>
              <SearchForm />
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t bg-card px-4 pb-4 md:hidden">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="block py-2 text-sm font-medium text-muted-foreground hover:text-primary"
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to={user ? "/dashboard" : "/practice-login"}
            className="flex items-center gap-1.5 py-2 text-sm font-medium text-primary"
            onClick={() => setMobileOpen(false)}
          >
            <LayoutDashboard className="h-4 w-4" />
            {user ? "My Dashboard" : "Practice Login"}
          </Link>
          <div className="mt-3 rounded-md border bg-muted/50 p-3">
            <p className="mb-2 text-sm font-medium text-foreground">Search by postcode</p>
            <SearchForm className="flex-col" />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
